import { ReactNode, useEffect, useState } from "react";
import { FormFieldsStrategyType } from "../types";
import FormItem from "antd/es/form/FormItem";
import { Checkbox, DatePicker, Input, Select } from "antd";
import { cn } from "../utils";
import dayjs from "dayjs";
import SearchCustomerByName from "@/components/Customer/SearchCustomerByName/SearchCustomerByName";
import { RangeSlider } from "@/components";
import TextArea from "antd/es/input/TextArea";
import CustomTypography from "@/components/Typography/CustomTypography";

const { Option } = Select;

export default function useGenerateFormElements(
  strategy: FormFieldsStrategyType[]
): { formElements: ReactNode[] } {
  const [formElements, setFormElements] = useState<ReactNode[]>([]);
  const generateFormElement = (el: FormFieldsStrategyType) => {
    switch (el.inputType) {
      case "text":
        return (
          <label
            className={cn("flex flex-col gap-[1px]", el.wrapperClassName)}
            key={el.name}
          >
            <div>
              <CustomTypography
                as="span"
                weight={"light"}
                className={el.labelClassName}
              >
                {el.label}
                {el.required && (
                  <CustomTypography as="span" color={"error"}>
                    *
                  </CustomTypography>
                )}
              </CustomTypography>
            </div>
            <FormItem
              name={el.name}
              initialValue={el.defaultValue ? el.defaultValue : ""}
              className={cn("!text-black", el.formItemClassName)}
              validateTrigger="onBlur"
              rules={[
                {
                  required: !!el.required,
                  message: "This field is required",
                },
                {
                  pattern: el.pattern || undefined,
                  message: el.patternMessage || undefined,
                },
              ]}
            >
              <Input
                readOnly={el.readonly}
                placeholder={el.label}
                disabled={el.readonly}
                variant="borderless"
                className={cn(
                  "bg-primaryDim px-4 py-3 !disabled:text-black text-black font-kanit font-light",
                  el.inputClassElement
                )}
              />
            </FormItem>
          </label>
        );
      case "select":
        return (
          <label className={cn(el.wrapperClassName)} key={el.name}>
            <CustomTypography
              as="span"
              weight={"light"}
              className={el.labelClassName}
            >
              {el.label}
              {el.required && (
                <CustomTypography as="span" color={"error"}>
                  *
                </CustomTypography>
              )}
            </CustomTypography>
            <FormItem
              validateTrigger="onBlur"
              name={el.name}
              initialValue={el.defaultValue ? el.defaultValue : ""}
              rules={[
                {
                  required: el.required,
                  message: "This field is required",
                },
                {
                  pattern: el.pattern || undefined,
                  message: el.patternMessage || undefined,
                },
              ]}
            >
              <Select
                value={el.defaultValue}
                disabled={el.readonly}
                removeIcon={el.readonly}
                rootClassName="!w-full"
                popupClassName="max-w-fit min-w-[290px]"
                className={cn(
                  "custom-select-antd text-base h-[46px] font-kanit font-light !border-none rounded-lg ",
                  el.selectClassElement
                )}
                variant="filled"
                style={{
                  backgroundColor: "#F3F9FC",
                }}
              >
                {el.options &&
                  el.options?.map((option) => (
                    <Option value={option.value} key={option.label}>
                      <CustomTypography as="span" weight={"light"}>
                        {option.label}
                      </CustomTypography>
                    </Option>
                  ))}
              </Select>
            </FormItem>
          </label>
        );
      case "date":
        return (
          <label
            className={cn("flex flex-col gap-[1px]", el.wrapperClassName)}
            key={el.name}
          >
            <CustomTypography
              as="span"
              weight={"light"}
              className={el.labelClassName}
            >
              {el.label}
              {el.required && (
                <CustomTypography as="span" color={"error"}>
                  *
                </CustomTypography>
              )}
            </CustomTypography>
            <FormItem
              name={el.name}
              validateTrigger="onBlur"
              initialValue={el.defaultValue ? dayjs(el.defaultValue) : null}
              rules={[
                {
                  required: el.required,
                  message: "This field is required",
                },
                {
                  type: "object",
                  message: "Invalid date format",
                },
              ]}
            >
              <DatePicker
                className="w-full h-[46px] rounded-lg bg-primaryDim text-base font-kanit !border-none !text-black hover:bg-primaryDim"
                placeholder={el.label}
                format={el.dateFormat || "YYYY-MM-DD"}
                inputReadOnly={el.readonly}
                minDate={dayjs(Date.now())}
                disabled={el.readonly}
              />
            </FormItem>
          </label>
        );
      case "customerName":
        return (
          <FormItem
            name={el.name}
            rules={[
              { required: el.required, message: "This field is required" },
            ]}
            key={el.name}
          >
            <SearchCustomerByName
              labelClassName={el.customerLabelClassName}
              className={el.pickerClassName}
            />
          </FormItem>
        );
      case "checkbox":
        return (
          <label className={cn(el.wrapperClassName)} key={el.name}>
            <CustomTypography
              as="span"
              weight={"light"}
              className={el.labelClassName}
            >
              {el.label}
            </CustomTypography>
            <FormItem
              validateTrigger="onBlur"
              name={el.name}
              initialValue={el.defaultValue ? el.defaultValue : ""}
              rules={[
                {
                  required: el.required,
                  message: "This field is required",
                },
                {
                  pattern: el.pattern || undefined,
                  message: el.patternMessage || undefined,
                },
              ]}
            >
              <Checkbox />
            </FormItem>
          </label>
        );
      case "range":
        return (
          <label className={cn(el.wrapperClassName)} key={el.name}>
            <CustomTypography
              as="span"
              weight={"light"}
              className={el.labelClassName}
            >
              {el.label}
              {/* {el.required && (
                <CustomTypography as="span" color={"error"}>
                  *
                </CustomTypography>
              )} */}
            </CustomTypography>
            <FormItem
              name={el.name}
              initialValue={[el.rangeMin, el.rangeMax]}
              getValueProps={(value) => {
                return { value: value ?? [el.rangeMin, el.rangeMax] };
              }}
              getValueFromEvent={(value) => {
                return value;
              }}
            >
              <RangeSlider
                min={el.rangeMin as number}
                max={el.rangeMax as number}
              />
            </FormItem>
          </label>
        );
      case "checkboxGroup":
        return (
          <label
            className={cn("flex flex-col gap-[1px]", el.wrapperClassName)}
            key={el.name}
          >
            <CustomTypography
              as="span"
              weight={"light"}
              className={el.labelClassName}
            >
              {el.label}
              {/* {el.required && (
                <CustomTypography as="span" color={"error"}>
                  *
                </CustomTypography>
              )} */}
            </CustomTypography>
            <FormItem
              name={el.name}
              initialValue={el.defaultValue ? el.defaultValue : []}
              validateTrigger="onBlur"
              rules={[
                {
                  required: !!el.required,
                  message: "This field is required",
                },
                {
                  validator: (_, value) => {
                    if (!Array.isArray(value)) {
                      return Promise.reject(new Error("Invalid selection"));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              getValueProps={(value) => {
                return { value: value ?? [] };
              }}
              getValueFromEvent={(value) => {
                return value || [];
              }}
            >
              <Checkbox.Group options={el.options} />
            </FormItem>
          </label>
        );
      case "textarea":
        return (
          <label>
            <CustomTypography
              as="span"
              weight={"light"}
              className={el.labelClassName}
            >
              {el.label}
            </CustomTypography>
            <FormItem
              name={el.name}
              initialValue={el.defaultValue}
              validateTrigger="onBlur"
            >
              <TextArea
                className="border-none pt-3 px-4 disabled:!text-black disabled:!bg-primaryDim"
                disabled={el.readonly}
              />
            </FormItem>
          </label>
        );
      default:
        break;
    }
  };

  useEffect(() => {
    setFormElements(() =>
      strategy.filter((el) => el !== null).map((el) => generateFormElement(el))
    );
  }, [strategy]);

  return { formElements };
}
